// NOTICE: KAPTION-INTEGRATION
// usage-analytics.TOKEN.3 — Vuex module that fetches + caches the short-lived
// analytics JWT and the worker URL. Cloned from store/modules/aiStudio.js but
// extended to (a) cache worker_url + expiry, (b) expose a getter the transport
// uses to know when to refresh (~80% of TTL), and (c) be best-effort: a failed
// fetch must never throw into product code.
import UsageAnalyticsAPI from '../../api/usageAnalytics';
import { TOKEN_REFRESH_RATIO } from 'dashboard/helper/usageAnalytics/constants';

const state = {
  token: null,
  workerUrl: null,
  // epoch ms when the cached token should be considered due-for-refresh
  // (mint time + expires_in * TOKEN_REFRESH_RATIO).
  refreshAt: null,
  uiFlags: {
    isFetchingToken: false,
  },
};

const getters = {
  getToken: $state => $state.token,
  getWorkerUrl: $state => $state.workerUrl,
  getUIFlags: $state => $state.uiFlags,
  // usage-analytics.TOKEN.3 — true when we have no token or it's past the
  // 80%-of-TTL refresh point. The transport calls this before each flush.
  isTokenExpiringSoon: $state => () => {
    if (!$state.token || !$state.refreshAt) return true;
    return Date.now() >= $state.refreshAt;
  },
};

const actions = {
  // Returns { token, workerUrl } or null on failure. NEVER throws.
  async fetchToken(
    { commit, state: $state, getters: $getters },
    { force = false } = {}
  ) {
    if (!force && $state.token && !$getters.isTokenExpiringSoon()) {
      return { token: $state.token, workerUrl: $state.workerUrl };
    }
    commit('setUIFlag', { isFetchingToken: true });
    try {
      const response = await UsageAnalyticsAPI.fetchToken();
      const {
        token,
        worker_url: workerUrl,
        expires_in: expiresIn,
      } = response.data;
      commit('setTokenData', {
        token,
        workerUrl,
        refreshAt:
          Date.now() + (expiresIn || 1800) * 1000 * TOKEN_REFRESH_RATIO,
      });
      commit('setUIFlag', { isFetchingToken: false });
      return { token, workerUrl };
    } catch (error) {
      // Best-effort: clear cache, swallow. Telemetry is non-critical.
      commit('setUIFlag', { isFetchingToken: false });
      return null;
    }
  },

  // Called when the worker rejects the token (401): drop it so the next flush
  // re-mints.
  invalidateToken({ commit }) {
    commit('clearToken');
  },
};

const mutations = {
  setTokenData($state, { token, workerUrl, refreshAt }) {
    $state.token = token;
    $state.workerUrl = workerUrl;
    $state.refreshAt = refreshAt;
  },
  clearToken($state) {
    $state.token = null;
    $state.refreshAt = null;
  },
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
